import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Layers, TrendingUp, AlertTriangle, CheckCircle2, DollarSign } from 'lucide-react';
import { SyncSourceLabel } from '@/components/shared/SyncSourceLabel';

interface Project {
  id: string;
  name: string;
  portfolio_id: string | null;
  program_name: string | null;
  budget_total: number | null;
  budget_spent: number | null;
  health_status: string | null;
  start_date: string | null;
  end_date: string | null;
  source: string | null;
  external_id: string | null;
  updated_at: string | null;
}

interface PortfolioGroup {
  id: string;
  label: string;
  projects: Project[];
  totalBudget: number;
  spentBudget: number;
  healthCounts: { green: number; amber: number; red: number };
}

function groupSyncSummary(projects: Project[]): { source: string; syncedAt: string } | null {
  const synced = projects.filter((p) => p.source && p.external_id);
  if (synced.length === 0) return null;
  const sources = Array.from(new Set(synced.map((p) => p.source as string)));
  const syncedAt = synced.reduce<string | null>((latest, p) => {
    if (!p.updated_at) return latest;
    return !latest || p.updated_at > latest ? p.updated_at : latest;
  }, null);
  if (!syncedAt) return null;
  return { source: sources.join(', '), syncedAt };
}

const HEALTH_CONFIG: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  green: { label: 'On Track',   className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: <CheckCircle2 className="h-3 w-3" /> },
  amber: { label: 'At Risk',    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',        icon: <AlertTriangle className="h-3 w-3" /> },
  red:   { label: 'Off Track',  className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',               icon: <AlertTriangle className="h-3 w-3" /> },
};

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

export const PortfolioView = () => {
  const { tenantId } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('projects_sync')
      .select('id, name, portfolio_id, program_name, budget_total, budget_spent, health_status, start_date, end_date, source, external_id, updated_at')
      .order('name');
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (error) {
      console.error('Failed to load projects for Portfolio view:', error);
    } else {
      setProjects((data ?? []) as Project[]);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Group by portfolio_id (or 'ungrouped')
  const groups: PortfolioGroup[] = Object.values(
    projects.reduce<Record<string, PortfolioGroup>>((acc, p) => {
      const key = p.portfolio_id ?? '__ungrouped__';
      const label = p.portfolio_id
        ? (p.program_name ?? p.portfolio_id)
        : 'Ungrouped Projects';

      if (!acc[key]) {
        acc[key] = { id: key, label, projects: [], totalBudget: 0, spentBudget: 0, healthCounts: { green: 0, amber: 0, red: 0 } };
      }
      acc[key].projects.push(p);
      acc[key].totalBudget += p.budget_total ?? 0;
      acc[key].spentBudget += p.budget_spent ?? 0;
      const h = (p.health_status ?? 'green') as 'green' | 'amber' | 'red';
      acc[key].healthCounts[h]++;
      return acc;
    }, {})
  );

  // Summary KPIs
  const totalProjects = projects.length;
  const totalBudget   = projects.reduce((s, p) => s + (p.budget_total ?? 0), 0);
  const totalSpent    = projects.reduce((s, p) => s + (p.budget_spent ?? 0), 0);
  const atRisk        = projects.filter(p => p.health_status === 'amber' || p.health_status === 'red').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Layers className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Portfolio Management</h2>
          <p className="text-muted-foreground mt-0.5">Aggregate view across all programmes and projects</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Projects</p>
              <p className="text-3xl font-bold mt-1">{totalProjects}</p>
            </div>
            <Layers className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Budget</p>
              <p className="text-3xl font-bold mt-1">{totalBudget > 0 ? fmt(totalBudget) : '—'}</p>
            </div>
            <DollarSign className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Budget Spent</p>
              <p className="text-3xl font-bold mt-1">
                {totalBudget > 0 ? `${Math.round((totalSpent / totalBudget) * 100)}%` : '—'}
              </p>
            </div>
            <TrendingUp className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">At Risk</p>
              <p className="text-3xl font-bold mt-1 text-amber-500">{atRisk}</p>
            </div>
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          </CardContent>
        </Card>
      </div>

      {/* Portfolio groups */}
      {projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Layers className="h-10 w-10 mb-4 opacity-30" />
            <p className="text-sm">No projects yet. Projects synced from integrations will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        groups.map((group) => {
          const syncSummary = groupSyncSummary(group.projects);
          return (
          <Card key={group.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{group.label}</CardTitle>
                  <CardDescription>{group.projects.length} project{group.projects.length !== 1 ? 's' : ''}</CardDescription>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {group.totalBudget > 0 && (
                    <span>{fmt(group.spentBudget)} / {fmt(group.totalBudget)}</span>
                  )}
                  <div className="flex gap-1.5">
                    {group.healthCounts.green > 0 && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] px-1.5">{group.healthCounts.green} on track</Badge>}
                    {group.healthCounts.amber > 0 && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] px-1.5">{group.healthCounts.amber} at risk</Badge>}
                    {group.healthCounts.red   > 0 && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] px-1.5">{group.healthCounts.red} off track</Badge>}
                  </div>
                </div>
              </div>
              {group.totalBudget > 0 && (
                <Progress value={(group.spentBudget / group.totalBudget) * 100} className="h-1.5 mt-2" />
              )}
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-6 py-2 text-start text-xs font-medium text-muted-foreground uppercase">Project</th>
                    <th className="px-6 py-2 text-start text-xs font-medium text-muted-foreground uppercase">Health</th>
                    <th className="px-6 py-2 text-end text-xs font-medium text-muted-foreground uppercase">Budget</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {group.projects.map((p) => {
                    const health = HEALTH_CONFIG[p.health_status ?? 'green'];
                    return (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-3 font-medium">{p.name}</td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${health.className}`}>
                            {health.icon}{health.label}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-end text-muted-foreground">
                          {p.budget_total ? `${fmt(p.budget_spent ?? 0)} / ${fmt(p.budget_total)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
            {syncSummary && (
              <div className="px-6 pb-4">
                <SyncSourceLabel source={syncSummary.source} syncedAt={syncSummary.syncedAt} />
              </div>
            )}
          </Card>
          );
        })
      )}
    </div>
  );
};

export default PortfolioView;
